import React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

/**
 * En-tête de page collant (sticky) — reste fixé en haut de la zone de contenu
 * lors du défilement pour garder titre + filtres/recherche accessibles.
 *
 * Un bouton chevron (haut/bas) permet à l'utilisateur de plier/déplier l'en-tête
 * pour libérer de l'espace vertical sans perdre la barre supérieure. L'état de
 * pliage est mémorisé par page (via `localStorage`, clé = chemin de la route).
 *
 * Compense le padding du `<main>` (p-4 sm:p-6 lg:p-8 dans Layout.jsx) via des
 * marges négatives sur les côtés + un top négatif, afin que le fond couvre
 * toute la largeur et se colle proprement à la barre supérieure de l'application.
 *
 * Usage :
 * ```jsx
 * <div className="space-y-6">
 *   <StickyPageHeader>
 *     <div>Titre &amp; sous-titre</div>
 *     <div>Filtres, recherche, boutons</div>
 *   </StickyPageHeader>
 *   ...reste de la page...
 * </div>
 * ```
 */
export default function StickyPageHeader({ children, className = "", testid, storageKey }) {
  const key = `sticky-header-collapsed:${storageKey || (typeof window !== "undefined" ? window.location.pathname : "default")}`;

  const [collapsed, setCollapsed] = React.useState(() => {
    if (typeof window === "undefined") return false;
    try { return window.localStorage.getItem(key) === "1"; } catch { return false; }
  });

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { window.localStorage.setItem(key, next ? "1" : "0"); } catch (e) { void e; }
      return next;
    });
  };

  return (
    <div
      className={
        "sticky top-[-1rem] sm:top-[-1.5rem] lg:top-[-2rem] z-30 " +
        "-mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 " +
        (collapsed ? "pt-4 sm:pt-6 lg:pt-8 pb-1 " : "pt-4 sm:pt-6 lg:pt-8 pb-4 ") +
        "bg-white/95 backdrop-blur-sm border-b border-zinc-200 " +
        "group " +
        className
      }
      data-testid={testid || "sticky-page-header"}
      data-collapsed={collapsed ? "true" : "false"}
    >
      {/*
       * Bug fix (iter89) : NE PAS démonter les enfants quand l'en-tête est
       * replié — certaines pages placent leur <Dialog> (création / édition)
       * dans les enfants ; un démontage rendait `setOpen(true)` inopérant
       * depuis les boutons hors en-tête (crayon "Modifier", "+" sur jour, etc.).
       * On masque visuellement au lieu de démonter — Radix Dialog utilise un
       * Portal vers document.body, donc l'ouverture depuis l'extérieur reste OK.
       */}
      <div className={collapsed ? "hidden" : "space-y-4"} aria-hidden={collapsed ? "true" : undefined}>
        {children}
      </div>
      <div className="flex justify-center -mb-2">
        <button
          type="button"
          onClick={toggle}
          className="inline-flex items-center gap-1 h-6 px-2 rounded-full border border-zinc-200 bg-white text-[10px] uppercase tracking-wider text-zinc-500 hover:text-zinc-900 hover:border-zinc-400 hover:bg-zinc-50 transition-colors"
          data-testid="sticky-header-toggle"
          aria-expanded={!collapsed}
          aria-label={collapsed ? "Déplier l'en-tête" : "Réduire l'en-tête"}
          title={collapsed ? "Déplier l'en-tête" : "Réduire l'en-tête"}
        >
          {collapsed ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
          <span className="hidden sm:inline">{collapsed ? "Déplier" : "Réduire"}</span>
        </button>
      </div>
    </div>
  );
}
